import {useCallback} from 'react';
import axios, {widgetEndpoints} from 'packages/common/utils/axiosWidget';

/**
 * @file Hooks d'empreinte bancaire de réservation pour le widget embarqué (auth par clé API).
 * @module packages.common.hooks.api.widgets.restaurants.Payment
 *
 * Câblé sur `axiosWidget` et les endpoints `externalApi.restaurant.reservation.*`.
 * Chaque hook retourne une fonction mémoïsée `(companyId, payload, apiKey) => Promise<Object>`
 * où `apiKey` est injectée dans l'en-tête `x-api-key`. Les hooks publics
 * équivalents (mêmes shapes de réponse) vivent dans `api/public/restaurants/Payment`.
 */

/**
 * Hook d'évaluation de l'exigence d'empreinte (chemin rapide, sans Stripe).
 *
 * Appelé par `usePaymentRequirement` à chaque changement d'heure/formules pour
 * décider si l'étape PAYMENT doit apparaître dans le stepper. Ne crée pas
 * d'intent Stripe — utiliser `usePreparePayment` au moment d'afficher PAYMENT.
 *
 * @return {Function} `(companyId, paymentData, apiKey) => Promise<Object>` —
 *   résout `{success, data: {required, amount?, source?, mode?, perGuestAmount?, flatAmount?, cancellationDeadline?}}`.
 */
export function useEvaluateCardHold() {
  return useCallback(async (companyId, paymentData, apiKey) => {
    const response = await axios.post(
        widgetEndpoints.externalApi.restaurant.reservation.evaluateCardHold(companyId),
        paymentData,
        {
          headers: {
            'x-api-key': apiKey,
          },
        },
    );
    return response.data;
  }, []);
}

/**
 * Hook pour préparer une empreinte bancaire pour une réservation (crée l'intent
 * Stripe). À appeler une seule fois à l'entrée de `PaymentStep`, pas à chaque
 * changement de sélection.
 *
 * @return {Function} `(companyId, paymentData, apiKey) => Promise<Object>` —
 *   résout `{success, data: {required, amount, clientSecret, stripeAccount, approach, intentType, ...}}`.
 */
export function usePreparePayment() {
  return useCallback(async (companyId, paymentData, apiKey) => {
    const response = await axios.post(
        widgetEndpoints.externalApi.restaurant.reservation.preparePayment(companyId),
        paymentData,
        {
          headers: {
            'x-api-key': apiKey,
          },
        },
    );
    return response.data;
  }, []);
}

/**
 * Hook pour confirmer une empreinte bancaire.
 *
 * @return {Function} `(companyId, confirmData, apiKey) => Promise<Object>` —
 *   résout la réponse JSON brute de l'endpoint `confirmPayment`.
 */
export function useConfirmPayment() {
  return useCallback(async (companyId, confirmData, apiKey) => {
    const response = await axios.post(
        widgetEndpoints.externalApi.restaurant.reservation.confirmPayment(companyId),
        confirmData,
        {
          headers: {
            'x-api-key': apiKey,
          },
        },
    );
    return response.data;
  }, []);
}

